import React, { useState, useEffect } from 'react';
import { FiSearch, FiDownload, FiFile, FiClock, FiCalendar, FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi';
import { reportService, documentService, userService } from '../../services/api';
import { exportDetailedReport, exportMultiSheet } from '../../utils/exportExcel';
import { format, parseISO } from 'date-fns';
import './Admin.css';
import './ExEmployees.css';

const ExEmployees = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  // Documents tab state
  const [exEmployees, setExEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  useEffect(() => {
    fetchExEmployeeReport();
    fetchExEmployeeList();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      fetchDocuments(selectedEmployee);
    }
  }, [selectedEmployee]);

  const fetchExEmployeeReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportService.getExEmployees();
      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load ex-employee report');
    } finally {
      setLoading(false);
    }
  };

  const fetchExEmployeeList = async () => {
    try {
      const response = await userService.getAll({ isActive: 'false', limit: 1000 });
      const users = response.data.users || [];
      setExEmployees(users);
      if (users.length > 0) {
        setSelectedEmployee(users[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch ex-employees:', err);
    }
  };

  const fetchDocuments = async (userId) => {
    try {
      setDocsLoading(true);
      const response = await documentService.getAll({ userId });
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocuments([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await documentService.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
    }
  };

  const toggleExpand = (empId) => {
    setExpandedEmployee(expandedEmployee === empId ? null : empId);
  };

  const filteredEmployees = (reportData?.employeeDetails || []).filter(emp =>
    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExport = () => {
    if (!reportData?.employeeDetails) return;
    const sheets = [];

    // Overview sheet
    sheets.push({
      name: 'Ex-Employees Overview',
      data: reportData.roster.map(e => ({
        Name: e.name,
        Email: e.email,
        Department: e.department,
        'Job Title': e.jobTitle,
        'Employment Type': e.employmentType,
        'Join Date': e.joinDate || '',
        'End Date': e.endDate || '',
      })),
    });

    // Per-employee detail sheets
    reportData.employeeDetails.forEach(emp => {
      const data = [];
      // Timesheet rows
      if (emp.timesheet.entries.length > 0) {
        emp.timesheet.entries.forEach(entry => {
          entry.tasks.forEach((task, i) => {
            data.push({
              Type: i === 0 ? 'Timesheet' : '',
              Date: i === 0 ? entry.date : '',
              Detail: task.taskName,
              Hours: task.hours,
              Status: i === 0 ? entry.status : '',
              Notes: task.description || '',
            });
          });
          if (entry.tasks.length === 0) {
            data.push({ Type: 'Timesheet', Date: entry.date, Detail: '—', Hours: entry.totalHours, Status: entry.status, Notes: '' });
          }
        });
      }
      // Leave rows
      if (emp.leave.records.length > 0) {
        emp.leave.records.forEach(r => {
          data.push({
            Type: 'Leave',
            Date: `${r.startDate} to ${r.endDate}`,
            Detail: r.leaveType,
            Hours: `${r.daysCount}d`,
            Status: r.status,
            Notes: r.reason,
          });
        });
      }
      if (data.length > 0) {
        const name = emp.name.replace(/[\\/*?[\]:]/g, '').substring(0, 28);
        sheets.push({ name: `EX - ${name}`, data });
      }
    });

    if (sheets.length === 0) {
      sheets.push({ name: 'Empty', data: [{ Note: 'No ex-employee data found.' }] });
    }
    exportMultiSheet(sheets, 'Ex_Employee_Report');
  };

  const getDocCategory = (type) => {
    const map = { 'address-proof': 'Address Proof', 'id-proof': 'ID Proof', passport: 'Passport', visa: 'Visa', other: 'Other' };
    return map[type] || type;
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h1>Ex-Employees</h1>
          <p className="header-subtitle">
            {reportData?.roster?.length || 0} former employee{(reportData?.roster?.length || 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport} disabled={!reportData}>
          <FiDownload /> Export Excel
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="ex-tabs">
        <button className={`tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
          <FiClock size={16} /> Reports
        </button>
        <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>
          <FiFile size={16} /> Documents
        </button>
      </div>

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="ex-report-section">
          <div className="filters-section">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-spinner"><div className="spinner"></div><p>Loading ex-employee data...</p></div>
          ) : filteredEmployees.length === 0 ? (
            <div className="no-results"><p>No ex-employees found.</p></div>
          ) : (
            <div className="ex-employee-list">
              {filteredEmployees.map((emp) => (
                <div key={emp.id} className="ex-employee-card">
                  <div className="ex-employee-header" onClick={() => toggleExpand(emp.id)}>
                    <div className="ex-employee-info">
                      <div className="employee-avatar inactive">{emp.name.split(' ').map(n => n[0]).join('')}</div>
                      <div>
                        <h3>{emp.name}</h3>
                        <p className="emp-meta">
                          {emp.email} &bull; {emp.jobTitle || 'N/A'} &bull; {emp.department}
                          {emp.endDate && <> &bull; Left: {emp.endDate}</>}
                        </p>
                      </div>
                    </div>
                    <div className="ex-employee-summary">
                      <div className="summary-pill">
                        <FiClock size={14} />
                        <span>{emp.timesheet.totalHours.toFixed(1)} hrs</span>
                      </div>
                      <div className="summary-pill">
                        <FiCalendar size={14} />
                        <span>{emp.leave.totalDays} leave days</span>
                      </div>
                      {expandedEmployee === emp.id ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                    </div>
                  </div>

                  {expandedEmployee === emp.id && (
                    <div className="ex-employee-details">
                      <div className="detail-section">
                        <h4><FiClock size={16} /> Timesheet History ({emp.timesheet.daysWorked} days, {emp.timesheet.totalHours.toFixed(1)} total hours)</h4>
                        {emp.timesheet.entries.length > 0 ? (
                          <div className="table-wrapper">
                            <table className="data-table compact">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Status</th>
                                  <th>Hours</th>
                                  <th>Tasks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emp.timesheet.entries.map((entry, idx) => (
                                  <tr key={idx}>
                                    <td>{entry.date}</td>
                                    <td><span className={`status-badge ${entry.status}`}>{entry.status}</span></td>
                                    <td><strong>{entry.totalHours}</strong></td>
                                    <td>
                                      {entry.tasks.length > 0
                                        ? entry.tasks.map((t, i) => <div key={i}>{t.taskName} ({t.hours}h)</div>)
                                        : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="no-data">No timesheet records found.</p>
                        )}
                      </div>

                      <div className="detail-section">
                        <h4><FiCalendar size={16} /> Leave History ({emp.leave.totalRequests} requests, {emp.leave.totalDays} days)</h4>
                        {emp.leave.records.length > 0 ? (
                          <div className="table-wrapper">
                            <table className="data-table compact">
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Start</th>
                                  <th>End</th>
                                  <th>Days</th>
                                  <th>Reason</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emp.leave.records.map((rec, idx) => (
                                  <tr key={idx}>
                                    <td>{rec.leaveType}</td>
                                    <td>{rec.startDate}</td>
                                    <td>{rec.endDate}</td>
                                    <td>{rec.daysCount}</td>
                                    <td className="reason-cell">{rec.reason}</td>
                                    <td><span className={`status-badge ${rec.status}`}>{rec.status}</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="no-data">No leave records found.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="ex-docs-section">
          <div className="filters-section">
            <div className="filter-group">
              <label>Select Ex-Employee:</label>
              <select
                className="filter-select"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                {exEmployees.length === 0 && <option value="">No ex-employees found</option>}
                {exEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName} — {emp.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {docsLoading ? (
            <div className="loading-spinner"><div className="spinner"></div><p>Loading documents...</p></div>
          ) : documents.length === 0 ? (
            <div className="no-results"><p>No documents found for this employee.</p></div>
          ) : (
            <div className="ex-docs-list">
              {documents.map(doc => (
                <div key={doc.id} className="ex-doc-card">
                  <div className="doc-info">
                    <FiFile size={20} />
                    <div>
                      <h4>{doc.originalName}</h4>
                      <p className="doc-meta">
                        {getDocCategory(doc.documentType)}
                        {doc.uploadDate && ` &bull; Uploaded ${format(parseISO(doc.uploadDate), 'MMM d, yyyy')}`}
                        {doc.expiryDate && ` &bull; Expires ${format(parseISO(doc.expiryDate), 'MMM d, yyyy')}`}
                      </p>
                      {doc.notes && <p className="doc-notes">{doc.notes}</p>}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-secondary" onClick={() => handleDownloadDocument(doc.id, doc.originalName)}>
                    <FiDownload /> Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExEmployees;
